import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  api,
  type MarketListing,
  type MarketListingsQuery,
  type MarketSort,
  type MyCardInstance,
} from "@/lib/api";
import { patchProfileSummary } from "@/components/layout/useProfile";
import { authClient } from "@/lib/auth";
import { resolveAuthenticated } from "@/lib/devConfig";
import { fmtRound } from "@/lib/format";

export type MarketStats = {
  volume24h: number;
  trades24h: number;
  openListings: number;
};

/** Mono IP formatter — integers with en-US thousands separators. */
export const formatIp = fmtRound;

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * Marketplace data + actions (M2 live trading — docs/MARKETPLACE_PLAN.md).
 * One source of truth for the page: open listings + 24h stats (public),
 * my card instances (authenticated), and the three mutations. Every mutation
 * disables its own button via `pending` keys and re-fetches server state on
 * success; backend error strings surface verbatim (EN) through sonner.
 *
 * `listings: false` = instances-only mode (ProfilePage's Listings tab renders
 * only SellSection): skips the public list_market_listings + 24h-stats fetch
 * entirely and turns refreshListings into a no-op — the list/cancel success
 * paths still update the instance rows locally, and /marketplace re-syncs the
 * public feed on its own mount.
 */
export function useMarketplace({ listings: listingsEnabled = true }: { listings?: boolean } = {}) {
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const authenticated = resolveAuthenticated(session?.user);

  const [listings, setListings] = useState<MarketListing[]>([]);
  const [stats, setStats] = useState<MarketStats | null>(null);
  const [listingsLoading, setListingsLoading] = useState(listingsEnabled);
  const [listingsError, setListingsError] = useState<string | null>(null);

  const [instances, setInstances] = useState<MyCardInstance[] | null>(null);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [instancesError, setInstancesError] = useState<string | null>(null);

  /** Busy mutation keys — `listing:{id}` (buy/cancel) and `instance:{id}` (list). */
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const markPending = useCallback((key: string, busy: boolean) => {
    setPending((prev) => {
      const next = new Set(prev);
      if (busy) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  // 서버측 필터/정렬/keyset 커서 — 필터는 첫 60개가 아니라 전체 리스팅에 적용된다.
  const [agentFilter, setAgentFilter] = useState<string | null>(null);
  const [finishFilter, setFinishFilter] = useState<string>("all");
  const [sort, setSort] = useState<MarketSort>("latest");
  const [nextCursor, setNextCursor] = useState<{ id: number; price?: number } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Request-staleness guards — bumped by every fresh refreshListings/refreshInstances
  // call so a slower, superseded response (filter switched mid-flight, signed out
  // mid-fetch) can be detected and dropped instead of splicing into current state.
  const listingsGeneration = useRef(0);
  const instancesGeneration = useRef(0);

  const listingsQuery = useCallback(
    (): MarketListingsQuery => ({
      agentId: agentFilter ?? undefined,
      finish: finishFilter === "all" ? undefined : finishFilter,
      sort,
    }),
    [agentFilter, finishFilter, sort],
  );

  const readCursor = (res: { nextCursorId?: number; nextCursorPrice?: number }) =>
    res.nextCursorId != null ? { id: res.nextCursorId, price: res.nextCursorPrice } : null;

  const refreshListings = useCallback(async () => {
    if (!listingsEnabled) return; // instances-only mode — mutations' re-sync no-ops
    const generation = ++listingsGeneration.current;
    try {
      const res = await api.listMarketListings(listingsQuery());
      if (generation !== listingsGeneration.current) return; // superseded by a newer filter/sort
      setListings(res.listings);
      setStats({
        volume24h: res.volume24h,
        trades24h: res.trades24h,
        openListings: res.openListings,
      });
      setNextCursor(readCursor(res));
      setListingsError(null);
    } catch (err) {
      if (generation !== listingsGeneration.current) return;
      // Backend down / not deployed yet — render empty with a retry line, never crash.
      setListingsError(errorMessage(err, "Couldn't load listings."));
    } finally {
      if (generation === listingsGeneration.current) setListingsLoading(false);
    }
  }, [listingsQuery, listingsEnabled]);

  useEffect(() => {
    if (!listingsEnabled) return;
    setListingsLoading(true);
    void refreshListings();
  }, [refreshListings, listingsEnabled]);

  /** 다음 페이지 append — 커서가 없으면 no-op. */
  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    // Pin to the in-flight generation — if refreshListings fires (filter/sort
    // changed) before this resolves, the response below is stale and dropped
    // instead of splicing the old filter's page onto the new grid.
    const generation = listingsGeneration.current;
    setLoadingMore(true);
    try {
      const res = await api.listMarketListings({
        ...listingsQuery(),
        cursorId: nextCursor.id,
        cursorPrice: nextCursor.price,
      });
      if (generation !== listingsGeneration.current) return;
      setListings((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        return [...prev, ...res.listings.filter((row) => !seen.has(row.id))];
      });
      setNextCursor(readCursor(res));
    } catch (err) {
      if (generation === listingsGeneration.current) {
        toast.error(errorMessage(err, "Couldn't load more listings."));
      }
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, listingsQuery]);

  const refreshInstances = useCallback(async () => {
    const generation = ++instancesGeneration.current;
    try {
      const res = await api.getMyCardInstances();
      if (generation !== instancesGeneration.current) return; // signed out mid-fetch
      setInstances(res.instances);
      setInstancesError(null);
    } catch (err) {
      if (generation !== instancesGeneration.current) return;
      setInstancesError(errorMessage(err, "Couldn't load your cards."));
    } finally {
      if (generation === instancesGeneration.current) setInstancesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sessionPending) return;
    if (!authenticated) {
      // Invalidate any in-flight fetch from a just-ended signed-in session so its
      // response can't resurrect the owned-card list after sign-out.
      instancesGeneration.current++;
      setInstances(null);
      setInstancesLoading(false);
      setInstancesError(null);
      return;
    }
    setInstancesLoading(true);
    void refreshInstances();
  }, [authenticated, sessionPending, session?.user?.id, refreshInstances]);

  /** Buy an open listing — optimistic grid removal, then server re-sync. */
  const buy = useCallback(
    async (listing: MarketListing) => {
      const key = `listing:${listing.id}`;
      markPending(key, true);
      try {
        const result = await api.buyMarketListing(listing.id);
        patchProfileSummary({ ip: result.ipBalance });
        setListings((prev) => prev.filter((row) => row.id !== listing.id));
        setStats((prev) =>
          prev ? { ...prev, openListings: Math.max(0, prev.openListings - 1) } : prev,
        );
        toast.success("+1 card acquired");
        await Promise.all([refreshListings(), instances !== null ? refreshInstances() : null]);
      } catch (err) {
        toast.error(errorMessage(err, "Purchase failed."));
        // Listing may have just been sold/cancelled by someone else — re-sync
        // so a now-dead listing doesn't stay in the grid for an infinite retry.
        await refreshListings();
      } finally {
        markPending(key, false);
      }
    },
    [markPending, refreshListings, refreshInstances, instances],
  );

  /** List one of my instances at a fixed integer IP price. */
  const list = useCallback(
    async (instance: MyCardInstance, price: number) => {
      const key = `instance:${instance.instanceId}`;
      markPending(key, true);
      try {
        const { listing } = await api.createMarketListing(instance.instanceId, price);
        setInstances((prev) =>
          prev?.map((row) =>
            row.instanceId === instance.instanceId
              ? { ...row, listed: true, listingId: listing.id, price: listing.price }
              : row,
          ) ?? prev,
        );
        toast.success(`Listed — ${formatIp(price)} IP`);
        await refreshListings();
      } catch (err) {
        toast.error(errorMessage(err, "Listing failed."));
      } finally {
        markPending(key, false);
      }
    },
    [markPending, refreshListings],
  );

  /** Cancel my own listing — from the listings grid or the sell section. */
  const cancel = useCallback(
    async (listingId: number) => {
      const key = `listing:${listingId}`;
      markPending(key, true);
      try {
        await api.cancelMarketListing(listingId);
        setListings((prev) => prev.filter((row) => row.id !== listingId));
        setInstances((prev) =>
          prev?.map((row) =>
            row.listingId === listingId
              ? { ...row, listed: false, listingId: null, price: null }
              : row,
          ) ?? prev,
        );
        toast.success("Listing cancelled");
        await Promise.all([refreshListings(), instances !== null ? refreshInstances() : null]);
      } catch (err) {
        toast.error(errorMessage(err, "Cancel failed."));
      } finally {
        markPending(key, false);
      }
    },
    [markPending, refreshListings, refreshInstances, instances],
  );

  return {
    authenticated,
    sessionPending,
    listings,
    stats,
    listingsLoading,
    listingsError,
    refreshListings,
    agentFilter,
    setAgentFilter,
    finishFilter,
    setFinishFilter,
    sort,
    setSort,
    hasMore: nextCursor !== null,
    loadMore,
    loadingMore,
    instances,
    instancesLoading,
    instancesError,
    refreshInstances,
    pending,
    buy,
    list,
    cancel,
  };
}
